import './InputWithStyle.scss';
import { useState, useRef, useEffect } from 'react';
import image_magnifying_glass from '../../assets/resources/custom/images/magnifying_glass_white.png';
import image_micro from '../../assets/resources/custom/images/micro.png';
import { HcContext } from "../../types/hccontext";

interface InputWithStyleProps extends React.InputHTMLAttributes<HTMLInputElement> {
    placeholder?: string;
    onValidation?: (value?: string) => void;
    value: string | undefined;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    language: string;
    hcContext: HcContext;
    height?: string;
    width?: string;
    hasSearchButton?: boolean;
    autoFocus?: boolean;
    title?: string;
    useMicrophone?: boolean;
}

function InputWithStyle({
    placeholder = "",
    onValidation,
    value,
    onChange,
    language,
    hcContext,
    height = '40px',
    width = '100%',
    hasSearchButton = false,
    autoFocus = false,
    title = '',
    useMicrophone = false,
}: Readonly<InputWithStyleProps>) {

    // --- State and Refs (unchanged) ---
    const [isRecording, setIsRecording] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);

    // --- useEffect for cleanup (unchanged) ---
    useEffect(() => {
        return () => {
            stopStreaming();
        };
    }, []);

    // --- startStreaming (unchanged) ---
    const startStreaming = async () => {
        if (!hcContext.backendUrlPython || !onChange) {
            setError("Backend URL not configured or onChange handler missing.");
            return;
        }
        console.log("Starting streaming...");
        setError(null);
        setIsRecording(true);
        const wsUrl = `${hcContext.backendUrlPython}/listen?lang=${language}`;

        try {
            socketRef.current = new WebSocket(wsUrl);

            socketRef.current.onopen = async () => {
                console.log("WebSocket connected to", wsUrl);
                const context = new AudioContext();
                audioContextRef.current = context;
                try {
                    await context.audioWorklet.addModule('/audio-processor.js');
                } catch (e) {
                    throw new Error(`Failed to load audio processor module: ${e}`);
                }
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        // Ask the browser to remove echo
                        echoCancellation: true,

                        // Bonus : Also requests background noise suppression
                        noiseSuppression: true,

                        // Bonus : Requests automatic volume adjustment
                        autoGainControl: true
                    }
                });
                audioStreamRef.current = stream;
                const source = context.createMediaStreamSource(stream);
                const workletNode = new AudioWorkletNode(context, 'audio-processor');
                audioWorkletNodeRef.current = workletNode;
                workletNode.port.onmessage = (event) => {
                    const pcmData: Float32Array = event.data;
                    if (socketRef.current?.readyState === WebSocket.OPEN) {
                        socketRef.current.send(pcmData.buffer);
                    }
                };
                source.connect(workletNode);
            };

            // --- MODIFIED: onmessage ---
            socketRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === "transcription") {
                    const transcribedText = data.text.trim();
                    console.log("Transcription:", transcribedText);

                    if (transcribedText) {
                        const newText = value ? `${value} ${transcribedText}` : transcribedText;
                        const syntheticEvent = {
                            target: { value: newText }
                        } as React.ChangeEvent<HTMLInputElement>;

                        onChange(syntheticEvent);
                        onValidation?.(newText);
                    }
                }
            };

            socketRef.current.onerror = (err) => {
                console.error("WebSocket error:", err);
                setError("WebSocket error occurred.");
                stopStreaming();
            };
            socketRef.current.onclose = () => {
                console.log("WebSocket disconnected.");
                setIsRecording(false);
            };
        } catch (err: any) {
            console.error("Error during streaming setup:", err);
            setError(`Error: ${err.message}`);
            setIsRecording(false);
        }
    };

    // --- stopStreaming (unchanged) ---
    const stopStreaming = () => {
        if (!isRecording && !socketRef.current) return;
        console.log("Stop of the streaming...");
        setIsRecording(false);
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        if (audioWorkletNodeRef.current) {
            audioWorkletNodeRef.current.disconnect();
            audioWorkletNodeRef.current = null;
        }
        if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
            audioStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
    };

    // --- handleToggleStreaming (unchanged) ---
    const handleToggleStreaming = () => {
        if (isRecording) {
            stopStreaming();
        } else {
            startStreaming();
        }
    };

    // --- MODIFIED: onInputKeyPressed ---
    function onInputKeyPressed(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.keyCode === 13 && onValidation) {
            e.preventDefault();
            onValidation(value); 
        }
    }

    // --- Rendering ---
    const showSearchButton = hasSearchButton && !useMicrophone && onValidation;
    const showMicroButton = useMicrophone && hcContext.backendUrlPython;
    const showButton = showSearchButton || showMicroButton;

    const buttonIcon = useMicrophone ? image_micro : image_magnifying_glass;

    const handleSearchClick = () => {
        onValidation?.(value);
    };

    // The onClick handler is now chosen between streaming or search
    const onButtonClick = useMicrophone ? handleToggleStreaming : handleSearchClick;

    let buttonTitle = '';
    if (useMicrophone) {
        buttonTitle = isRecording ? 'Arrêter l\'enregistrement' : 'Commencer l\'enregistrement';
    } else if (hasSearchButton) {
        buttonTitle = 'Rechercher';
    }

    const currentInputTitle = error ? `Error: ${error}` : title;
    const buttonClassName = `hc-input-search-btn ${isRecording ? 'hc-recording' : ''}`;
    const validationBtnSize = showButton ? height : '0px';
    const containerStyle: React.CSSProperties = { height: height, width: width, position: 'relative' };
    const inputStyle: React.CSSProperties = {
        paddingLeft: 10, paddingRight: validationBtnSize
    };
    const buttonStyle: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', width: height };

    return (
        <span style={containerStyle} >
            <div style={containerStyle}>
                <input
                    type="text"
                    className={`input hc-input-search ${hcContext.isDarkTheme ? 'hc-dark-theme' : 'hc-light-theme'}`}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onKeyUp={onInputKeyPressed}
                    style={inputStyle}
                    autoFocus={autoFocus}
                    title={currentInputTitle}
                />
                {
                    showButton &&
                    <div className={buttonClassName} style={buttonStyle} title={buttonTitle}>
                        <img
                            src={buttonIcon}
                            alt={buttonTitle}
                            height="20px"
                            width="20px"
                            onClick={onButtonClick} // This now calls the correct handler
                            style={{ cursor: 'pointer' }}
                        />
                    </div>
                }
            </div>
        </span>
    )
}

export default InputWithStyle;